import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Calendar as CalendarIcon, Clock, Plus, X, Loader2, Palmtree, Ban, CheckCircle, Lock } from 'lucide-react';
import { format, addMonths, isSameMonth, startOfMonth, endOfMonth, eachDayOfInterval, isBefore, isToday, startOfDay, getDay, isSameDay } from 'date-fns';
import { it } from 'date-fns/locale';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';

export default function AdminAvailability() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [blockReason, setBlockReason] = useState('');
  const [blockType, setBlockType] = useState('full_day');
  
  // Availability data
  const [blockedDates, setBlockedDates] = useState([]);
  const [bookedDates, setBookedDates] = useState([]);
  const [dateStatuses, setDateStatuses] = useState({});

  // Bookings per day detail
  const [dayBookings, setDayBookings] = useState([]);

  useEffect(() => {
    fetchAvailability();
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await api.get('/admin/settings');
      const data = response.data;
      
      // Ensure booking_settings.booking_mode is synced with calendar_mode for backwards compat
      if (data && !data.booking_settings?.booking_mode && data.calendar_mode) {
        data.booking_settings = data.booking_settings || {};
        data.booking_settings.booking_mode = data.calendar_mode;
      }
      
      setSettings(data);
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const fetchAvailability = async () => {
    try {
      const response = await api.get('/admin/availability');
      setBlockedDates(response.data.blocked_dates || []);
      setBookedDates(response.data.booked_dates || []);
      setDateStatuses(response.data.date_statuses || {});
    } catch (error) {
      console.error('Error:', error);
      toast.error('Errore nel caricamento disponibilità');
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async (newSettings) => {
    setSaving(true);
    try {
      await api.put('/admin/settings', newSettings);
      toast.success('Impostazioni salvate');
      fetchSettings();
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const updateBookingSettings = (field, value) => {
    // For booking_mode, update both booking_settings.booking_mode AND calendar_mode for backwards compat
    const newSettings = {
      ...settings,
      booking_settings: {
        ...settings.booking_settings,
        [field]: value
      }
    };
    
    // Sync calendar_mode with booking_mode at root level for backend compatibility
    if (field === 'booking_mode') {
      newSettings.calendar_mode = value;
    }
    
    saveSettings(newSettings);
  };

  const addTimeSlot = () => {
    const slots = settings?.booking_settings?.time_slots || [];
    const newSlots = [...slots, '09:00-12:00'];
    updateBookingSettings('time_slots', newSlots);
  };

  const updateTimeSlot = (index, value) => {
    const slots = [...(settings?.booking_settings?.time_slots || [])];
    slots[index] = value;
    updateBookingSettings('time_slots', slots);
  };

  const removeTimeSlot = (index) => {
    const slots = [...(settings?.booking_settings?.time_slots || [])];
    slots.splice(index, 1);
    updateBookingSettings('time_slots', slots);
  };

  const toggleWorkingDay = (day) => {
    const workingDays = settings?.booking_settings?.working_days || [1,2,3,4,5];
    let newDays;
    if (workingDays.includes(day)) {
      newDays = workingDays.filter(d => d !== day);
    } else {
      newDays = [...workingDays, day].sort();
    }
    updateBookingSettings('working_days', newDays);
  };

  const blockDate = async () => {
    if (!selectedDate) return;

    try {
      await api.post('/admin/availability/block', {
        date: format(selectedDate, 'yyyy-MM-dd'),
        reason: blockReason,
        type: blockType
      });
      toast.success('Data bloccata');
      setShowBlockModal(false);
      setBlockReason('');
      setBlockType('full_day');
      setSelectedDate(null);
      fetchAvailability();
    } catch (error) {
      console.error('Block error:', error);
      toast.error('Errore nel blocco della data');
    }
  };

  const unblockDate = async (dateStr) => {
    try {
      await api.delete(`/admin/availability/block/${dateStr}`);
      toast.success('Blocco rimosso');
      fetchAvailability();
    } catch (error) {
      console.error('Unblock error:', error);
      toast.error('Errore nella rimozione del blocco');
    }
  };

  const getDayStatus = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    
    // Check if it's today or in past
    if (isBefore(date, startOfDay(new Date())) && !isToday(date)) {
      return { status: 'past', label: 'Passato', color: 'bg-gray-100 text-gray-400' };
    }

    // Check if blocked
    if (blockedDates.includes(dateStr)) {
      return { status: 'blocked', label: 'Bloccato', color: 'bg-red-100 text-red-700 border-red-300' };
    }

    // Check from server status
    const serverStatus = dateStatuses[dateStr];
    if (serverStatus) {
      if (serverStatus === 'booked' || serverStatus === 'occupato') {
        return { status: 'booked', label: 'Occupato', color: 'bg-orange-100 text-orange-700 border-orange-300' };
      }
      if (serverStatus === 'richiesto') {
        return { status: 'requested', label: 'Richiesto', color: 'bg-yellow-100 text-yellow-700 border-yellow-300' };
      }
    }

    // Check if booked
    if (bookedDates.includes(dateStr)) {
      return { status: 'booked', label: 'Occupato', color: 'bg-orange-100 text-orange-700 border-orange-300' };
    }

    // Check if working day
    const dayOfWeek = getDay(date);
    const adjustedDay = dayOfWeek === 0 ? 7 : dayOfWeek;
    const workingDays = settings?.booking_settings?.working_days || [1,2,3,4,5];
    
    if (!workingDays.includes(adjustedDay)) {
      return { status: 'non_working', label: 'Non lavorativo', color: 'bg-gray-50 text-gray-400' };
    }

    return { status: 'available', label: 'Disponibile', color: 'bg-green-50 text-green-700 border-green-300 hover:bg-green-100' };
  };

  const handleDayClick = async (date) => {
    const status = getDayStatus(date);
    if (status.status === 'past') return;

    setSelectedDate(date);
    
    // Fetch bookings for this date if exists
    const dateStr = format(date, 'yyyy-MM-dd');
    try {
      const response = await api.get(`/admin/bookings?date=${dateStr}`);
      setDayBookings(response.data || []);
    } catch (error) {
      setDayBookings([]);
    }

    if (status.status === 'available' || status.status === 'non_working') {
      setShowBlockModal(true);
    }
  };

  const renderCalendar = () => {
    const start = startOfMonth(currentMonth);
    const end = endOfMonth(currentMonth);
    const days = eachDayOfInterval({ start, end });
    
    // Get the first day of the month (0 = Sun, 1 = Mon, etc.)
    const startDay = getDay(start);
    const adjustedStartDay = startDay === 0 ? 6 : startDay - 1; // Adjust for Monday start

    const dayNames = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'];

    return (
      <div className="bg-card border rounded-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <Button variant="outline" size="sm" onClick={() => setCurrentMonth(addMonths(currentMonth, -1))}>
            ← Mese prec.
          </Button>
          <h3 className="font-heading text-xl">{format(currentMonth, 'MMMM yyyy', { locale: it })}</h3>
          <Button variant="outline" size="sm" onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}>
            Mese succ. →
          </Button>
        </div>

        <div className="grid grid-cols-7 gap-1 mb-2">
          {dayNames.map((day) => (
            <div key={day} className="text-center text-xs font-medium text-muted-foreground py-2">
              {day}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-1">
          {/* Empty cells for days before start of month */}
          {[...Array(adjustedStartDay)].map((_, i) => (
            <div key={`empty-${i}`} className="aspect-square" />
          ))}

          {/* Calendar days */}
          {days.map((day) => {
            const { status, label, color } = getDayStatus(day);
            const dateStr = format(day, 'yyyy-MM-dd');
            const isSelected = selectedDate && isSameDay(day, selectedDate);
            
            return (
              <button
                key={dateStr}
                onClick={() => handleDayClick(day)}
                disabled={status === 'past'}
                className={`aspect-square p-1 relative text-sm rounded transition-all border ${color} ${isSelected ? 'ring-2 ring-primary ring-offset-2' : ''}`}
                title={label}
              >
                <span className="font-medium">{format(day, 'd')}</span>
                {status === 'blocked' && <Ban size={12} className="absolute bottom-1 right-1" />}
                {status === 'booked' && <Lock size={12} className="absolute bottom-1 right-1" />}
                {status === 'requested' && <Clock size={12} className="absolute bottom-1 right-1" />}
              </button>
            );
          })}
        </div>

        {/* Legend */}
        <div className="flex flex-wrap gap-4 mt-6 pt-4 border-t text-xs">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-green-50 border border-green-300" />
            <span>Disponibile</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-yellow-100 border border-yellow-300" />
            <span>Richiesto</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-orange-100 border border-orange-300" />
            <span>Occupato</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-red-100 border border-red-300" />
            <span>Bloccato</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-gray-50 border" />
            <span>Non lavorativo</span>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-muted-foreground">Caricamento...</div>
      </div>
    );
  }

  const bookingSettings = settings?.booking_settings || {};
  const workingDays = bookingSettings.working_days || [1,2,3,4,5];
  const timeSlots = bookingSettings.time_slots || [];

  return (
    <div data-testid="admin-availability-page">
      <div className="mb-8">
        <h1 className="font-heading text-3xl mb-2">Disponibilità & Calendario</h1>
        <p className="text-muted-foreground">Gestisci la tua disponibilità e le impostazioni di prenotazione</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left: Calendar */}
        <div className="lg:col-span-2">
          {renderCalendar()}

          {/* Blocked Dates List */}
          {blockedDates.length > 0 && (
            <Card className="mt-6">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Palmtree size={18} />
                  Date Bloccate (Ferie/Non Disponibile)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {blockedDates.map((dateStr) => (
                    <span
                      key={dateStr}
                      className="inline-flex items-center gap-2 px-3 py-1 bg-red-50 text-red-700 rounded text-sm"
                    >
                      {format(new Date(dateStr), 'dd MMM yyyy', { locale: it })}
                      <button onClick={() => unblockDate(dateStr)} className="hover:text-red-900">
                        <X size={14} />
                      </button>
                    </span>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Right: Settings */}
        <div className="space-y-6">
          {/* Booking Mode */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Modalità Prenotazione</CardTitle>
              <CardDescription>
                Scegli come gestire le richieste di prenotazione
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label>Prenotazione Automatica</Label>
                  <p className="text-sm text-muted-foreground">
                    {bookingSettings.booking_mode === 'auto' 
                      ? 'Prenotazioni confermate e bloccate automaticamente'
                      : 'Richieste manuali da approvare'}
                  </p>
                </div>
                <Switch
                  checked={bookingSettings.booking_mode === 'auto'}
                  onCheckedChange={(checked) => updateBookingSettings('booking_mode', checked ? 'auto' : 'manual')}
                  data-testid="booking-mode-toggle"
                />
              </div>
            </CardContent>
          </Card>

          {/* Working Days */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Giorni Lavorativi</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {[
                  { day: 1, label: 'Lun' },
                  { day: 2, label: 'Mar' },
                  { day: 3, label: 'Mer' },
                  { day: 4, label: 'Gio' },
                  { day: 5, label: 'Ven' },
                  { day: 6, label: 'Sab' },
                  { day: 7, label: 'Dom' }
                ].map(({ day, label }) => (
                  <button
                    key={day}
                    onClick={() => toggleWorkingDay(day)}
                    className={`px-3 py-2 rounded text-sm font-medium transition-colors ${
                      workingDays.includes(day)
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted text-muted-foreground'
                    }`}
                    data-testid={`working-day-${day}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Time Slots */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base">Fasce Orarie</CardTitle>
                  <CardDescription>Slot disponibili per le prenotazioni</CardDescription>
                </div>
                <Button variant="outline" size="sm" onClick={addTimeSlot}>
                  <Plus size={14} className="mr-1" />
                  Aggiungi
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {timeSlots.length > 0 ? (
                <div className="space-y-3">
                  {timeSlots.map((slot, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <Clock size={16} className="text-muted-foreground" />
                      <Input
                        value={slot}
                        onChange={(e) => updateTimeSlot(index, e.target.value)}
                        placeholder="Es: 09:00-12:00"
                        className="flex-1"
                      />
                      <Button variant="ghost" size="sm" onClick={() => removeTimeSlot(index)}>
                        <X size={14} />
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground text-center py-4">
                  Nessuna fascia oraria definita. I clienti potranno prenotare per giornata intera.
                </p>
              )}
            </CardContent>
          </Card>

          {/* Advance Booking */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Anticipo Prenotazioni</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-sm">Giorni massimi in anticipo</Label>
                <Input
                  type="number"
                  min={1}
                  max={365}
                  value={bookingSettings.advance_booking_days || 90}
                  onChange={(e) => updateBookingSettings('advance_booking_days', parseInt(e.target.value) || 90)}
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-sm">Preavviso minimo (giorni)</Label>
                <Input
                  type="number"
                  min={0}
                  max={30}
                  value={bookingSettings.min_notice_days || 2}
                  onChange={(e) => updateBookingSettings('min_notice_days', parseInt(e.target.value) || 2)}
                  className="mt-1"
                />
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Block Date Modal */}
      <Dialog open={showBlockModal} onOpenChange={setShowBlockModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Blocca Data: {selectedDate && format(selectedDate, 'dd MMMM yyyy', { locale: it })}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label className="mb-2 block">Tipo blocco</Label>
              <Select value={blockType} onValueChange={setBlockType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="full_day">Giornata intera</SelectItem>
                  <SelectItem value="vacation">Ferie</SelectItem>
                  <SelectItem value="personal">Impegno personale</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="mb-2 block">Motivo (opzionale)</Label>
              <Input
                value={blockReason}
                onChange={(e) => setBlockReason(e.target.value)}
                placeholder="Es: Vacanza, Impegno personale..."
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBlockModal(false)}>
              Annulla
            </Button>
            <Button onClick={blockDate}>
              <Ban size={16} className="mr-2" />
              Blocca Data
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Day Details (when selected) */}
      {selectedDate && dayBookings.length > 0 && (
        <Dialog open={true} onOpenChange={() => setSelectedDate(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                Prenotazioni: {format(selectedDate, 'dd MMMM yyyy', { locale: it })}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3 py-4">
              {dayBookings.map((booking) => (
                <div key={booking.id} className="p-3 bg-muted rounded">
                  <div className="font-medium">{booking.first_name} {booking.last_name}</div>
                  <div className="text-sm text-muted-foreground">{booking.service_type}</div>
                  <div className="text-sm text-muted-foreground">{booking.time_slot || 'Giornata intera'}</div>
                  <span className={`inline-block mt-1 px-2 py-0.5 text-xs rounded ${
                    booking.status === 'confermata' ? 'bg-green-100 text-green-700' :
                    booking.status === 'richiesta' ? 'bg-yellow-100 text-yellow-700' :
                    'bg-gray-100 text-gray-700'
                  }`}>
                    {booking.status}
                  </span>
                </div>
              ))}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setSelectedDate(null)}>
                Chiudi
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
